"use client";

import type {
  CustomReturn,
  CustomReturnFormType,
  CustomReturnUserFormValues,
  DhlParcelPoint,
  OrderHistorySimple,
  OrderReturn,
  SelectedProducts,
} from "@workspace/ui/types";
import type { Dispatch } from "react";
import React, { createContext, useContext, useMemo, useReducer } from "react";

import { CustomReturnFormAPI, OrderReturnAPI } from "@/const/endPoint";
import { camelToSnakeCaseObject, snakeToCamelCaseObject } from "@/utils";
import axiosInstance from "@/utils/api/fetchData/axiosInstance";

export enum OrderReturnActionType {
  // Order Returns
  START_ORDER_RETURNS = "START_ORDER_RETURNS",
  SUCCESS_ORDER_RETURNS = "SUCCESS_ORDER_RETURNS",
  FAILURE_ORDER_RETURNS = "FAILURE_ORDER_RETURNS",

  // Custom Return
  START_CUSTOM_RETURN = "START_CUSTOM_RETURN",
  SUCCESS_CUSTOM_RETURN = "SUCCESS_CUSTOM_RETURN",
  FAILURE_CUSTOM_RETURN = "FAILURE_CUSTOM_RETURN",

  // Custom Return Form
  START_CUSTOM_RETURN_FORM = "START_CUSTOM_RETURN_FORM",
  SUCCESS_CUSTOM_RETURN_FORM = "SUCCESS_CUSTOM_RETURN_FORM",
  FAILURE_CUSTOM_RETURN_FORM = "FAILURE_CUSTOM_RETURN_FORM",

  // Create Custom Return
  START_CREATE_CUSTOM_RETURN = "START_CREATE_CUSTOM_RETURN",
  SUCCESS_CREATE_CUSTOM_RETURN = "SUCCESS_CREATE_CUSTOM_RETURN",
  FAILURE_CREATE_CUSTOM_RETURN = "FAILURE_CREATE_CUSTOM_RETURN",

  // Get Redirect Url
  START_GET_REDIRECT_URL = "START_GET_REDIRECT_URL",
  SUCCESS_GET_REDIRECT_URL = "SUCCESS_GET_REDIRECT_URL",
  FAILURE_GET_REDIRECT_URL = "FAILURE_GET_REDIRECT_URL",
}

export type OrderReturnState = {
  // Order Returns
  ordersToReturn: OrderHistorySimple[];
  orderReturns: OrderReturn[];
  loadingOrderReturns: boolean;
  errorOrderReturns: string | null;

  // Custom Return
  customReturn: CustomReturn | null;
  loadingCustomReturn: boolean;
  errorCustomReturn: string | null;

  // Custom Return Form
  customReturnForm: CustomReturnFormType | null;
  loadingCustomReturnForm: boolean;
  errorCustomReturnForm: string | null;

  // Create Custom Return
  loadingCreateCustomReturn: boolean;
  errorCreateCustomReturn: string | null;

  // Get Redirect Url
  loadingRedirectUrl: boolean;
  errorRedirectUrl: string | null;
};

const OrderReturnStateContext = createContext<OrderReturnState | undefined>(
  undefined,
);

type OrderReturnAction =
  // Order Returns
  | { type: OrderReturnActionType.START_ORDER_RETURNS }
  | {
      type: OrderReturnActionType.SUCCESS_ORDER_RETURNS;
      ordersToReturn: OrderHistorySimple[];
      orderReturns: OrderReturn[];
    }
  | { type: OrderReturnActionType.FAILURE_ORDER_RETURNS; error: string }
  // Custom Return
  | { type: OrderReturnActionType.START_CUSTOM_RETURN }
  | {
      type: OrderReturnActionType.SUCCESS_CUSTOM_RETURN;
      customReturn: CustomReturn | null;
    }
  | { type: OrderReturnActionType.FAILURE_CUSTOM_RETURN; error: string }
  // Custom Return Form
  | { type: OrderReturnActionType.START_CUSTOM_RETURN_FORM }
  | {
      type: OrderReturnActionType.SUCCESS_CUSTOM_RETURN_FORM;
      customReturnForm: CustomReturnFormType | null;
    }
  | { type: OrderReturnActionType.FAILURE_CUSTOM_RETURN_FORM; error: string }
  // Create Custom Return
  | {
      type: OrderReturnActionType.START_CREATE_CUSTOM_RETURN;
      idReturnedMethod: string;
    }
  | { type: OrderReturnActionType.SUCCESS_CREATE_CUSTOM_RETURN }
  | { type: OrderReturnActionType.FAILURE_CREATE_CUSTOM_RETURN; error: string }
  // Get Redirect Url
  | { type: OrderReturnActionType.START_GET_REDIRECT_URL }
  | { type: OrderReturnActionType.SUCCESS_GET_REDIRECT_URL }
  | { type: OrderReturnActionType.FAILURE_GET_REDIRECT_URL; error: string };

type OrderReturnDispatch = Dispatch<OrderReturnAction>;

const OrderReturnDispatchContext = createContext<
  OrderReturnDispatch | undefined
>(undefined);

const initialOrderReturnState: OrderReturnState = {
  ordersToReturn: [],
  orderReturns: [],
  loadingOrderReturns: false,
  errorOrderReturns: null,

  customReturn: null,
  loadingCustomReturn: false,
  errorCustomReturn: null,

  customReturnForm: null,
  loadingCustomReturnForm: false,
  errorCustomReturnForm: null,

  loadingCreateCustomReturn: false,
  errorCreateCustomReturn: null,

  // Get Redirect Url
  loadingRedirectUrl: false,
  errorRedirectUrl: null,
};

function orderReturnReducer(
  state: OrderReturnState,
  action: OrderReturnAction,
): OrderReturnState {
  switch (action.type) {
    // Order Returns
    case OrderReturnActionType.START_ORDER_RETURNS:
      return {
        ...state,
        loadingOrderReturns: true,
        errorOrderReturns: null,
      };
    case OrderReturnActionType.SUCCESS_ORDER_RETURNS:
      return {
        ...state,
        ordersToReturn: action.ordersToReturn,
        orderReturns: action.orderReturns,
        loadingOrderReturns: false,
        errorOrderReturns: null,
      };
    case OrderReturnActionType.FAILURE_ORDER_RETURNS:
      return {
        ...state,
        loadingOrderReturns: false,
        errorOrderReturns: action.error,
      };

    // Custom Return
    case OrderReturnActionType.START_CUSTOM_RETURN:
      return {
        ...state,
        loadingCustomReturn: true,
        errorCustomReturn: null,
      };
    case OrderReturnActionType.SUCCESS_CUSTOM_RETURN:
      return {
        ...state,
        customReturn: action.customReturn,
        loadingCustomReturn: false,
        errorCustomReturn: null,
      };
    case OrderReturnActionType.FAILURE_CUSTOM_RETURN:
      return {
        ...state,
        loadingCustomReturn: false,
        errorCustomReturn: action.error,
      };

    // Custom Return Form
    case OrderReturnActionType.START_CUSTOM_RETURN_FORM:
      return {
        ...state,
        loadingCustomReturnForm: true,
        errorCustomReturnForm: null,
      };
    case OrderReturnActionType.SUCCESS_CUSTOM_RETURN_FORM:
      return {
        ...state,
        customReturnForm: action.customReturnForm,
        loadingCustomReturnForm: false,
        errorCustomReturnForm: null,
      };
    case OrderReturnActionType.FAILURE_CUSTOM_RETURN_FORM:
      return {
        ...state,
        loadingCustomReturnForm: false,
        errorCustomReturnForm: action.error,
      };

    // Create Custom Return
    case OrderReturnActionType.START_CREATE_CUSTOM_RETURN: {
      const { idReturnedMethod } = action;
      const returnMethod = state.customReturnForm?.returnMethods.find(
        (method) => method.idReturnMethod === idReturnedMethod,
      );

      if (returnMethod) {
        const p24Amount = returnMethod.price;
        if (state.customReturnForm?.P24) {
          state.customReturnForm.P24.p24Amount = parseFloat(p24Amount);
        }
      }

      return {
        ...state,
        loadingCreateCustomReturn: true,
        errorCreateCustomReturn: null,
      };
    }
    case OrderReturnActionType.SUCCESS_CREATE_CUSTOM_RETURN:
      return {
        ...state,
        loadingCreateCustomReturn: false,
        errorCreateCustomReturn: null,
      };
    case OrderReturnActionType.FAILURE_CREATE_CUSTOM_RETURN:
      return {
        ...state,
        loadingCreateCustomReturn: false,
        errorCreateCustomReturn: action.error,
      };

    // Get Redirect Url
    case OrderReturnActionType.START_GET_REDIRECT_URL:
      return {
        ...state,
        loadingRedirectUrl: true,
        errorRedirectUrl: null,
      };
    case OrderReturnActionType.SUCCESS_GET_REDIRECT_URL:
      return {
        ...state,
        loadingRedirectUrl: false,
        errorRedirectUrl: null,
      };
    case OrderReturnActionType.FAILURE_GET_REDIRECT_URL:
      return {
        ...state,
        loadingRedirectUrl: false,
        errorRedirectUrl: action.error,
      };

    default:
      throw new Error("Unhandled action");
  }
}

export function OrderReturnProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(
    orderReturnReducer,
    initialOrderReturnState,
  );

  return (
    <OrderReturnDispatchContext.Provider value={dispatch}>
      <OrderReturnStateContext.Provider value={useMemo(() => state, [state])}>
        {children}
      </OrderReturnStateContext.Provider>
    </OrderReturnDispatchContext.Provider>
  );
}

export function useOrderReturnState() {
  const state = useContext(OrderReturnStateContext);
  if (!state) throw new Error("OrderReturnStateContext not found");
  return state;
}

export function useOrderReturnDispatch() {
  const dispatch = useContext(OrderReturnDispatchContext);
  if (!dispatch) throw new Error("OrderReturnDispatchContext not found");
  return dispatch;
}

const getOrderReturnsData = async (dispatch: OrderReturnDispatch) => {
  dispatch({ type: OrderReturnActionType.START_ORDER_RETURNS });
  try {
    const response = await axiosInstance.get(`/rest${OrderReturnAPI}`);

    if (response.data.psdata) {
      const formattedResponse = snakeToCamelCaseObject(response.data.psdata);

      dispatch({
        type: OrderReturnActionType.SUCCESS_ORDER_RETURNS,
        ordersToReturn: formattedResponse.ordersToReturn,
        orderReturns: formattedResponse.orderReturns,
      });
    } else {
      dispatch({
        type: OrderReturnActionType.FAILURE_ORDER_RETURNS,
        error: "No data received",
      });
    }
  } catch (error: any) {
    dispatch({
      type: OrderReturnActionType.FAILURE_ORDER_RETURNS,
      error: error.message,
    });
  }
};

const getCustomReturnData = async (
  idOrderReturn: string,
  dispatch: OrderReturnDispatch,
) => {
  dispatch({ type: OrderReturnActionType.START_CUSTOM_RETURN });
  try {
    const response = await axiosInstance.get(
      `/rest${OrderReturnAPI}?id_order_return=${idOrderReturn}`,
    );

    if (response.data.psdata) {
      const formattedResponse = snakeToCamelCaseObject(response.data.psdata);

      dispatch({
        type: OrderReturnActionType.SUCCESS_CUSTOM_RETURN,
        customReturn: formattedResponse,
      });
    } else {
      dispatch({
        type: OrderReturnActionType.FAILURE_CUSTOM_RETURN,
        error: "No data received",
      });
    }
  } catch (error: any) {
    dispatch({
      type: OrderReturnActionType.FAILURE_CUSTOM_RETURN,
      error: error.message,
    });
  }
};

const getCustomReturnFormData = async (
  idOrder: string,
  dispatch: OrderReturnDispatch,
) => {
  dispatch({ type: OrderReturnActionType.START_CUSTOM_RETURN_FORM });
  try {
    const response = await axiosInstance.get(
      `/rest${CustomReturnFormAPI}?id_order=${idOrder}`,
    );

    if (response.data.psdata) {
      const formattedResponse = snakeToCamelCaseObject(response.data.psdata);

      dispatch({
        type: OrderReturnActionType.SUCCESS_CUSTOM_RETURN_FORM,
        customReturnForm: formattedResponse,
      });
    } else {
      dispatch({
        type: OrderReturnActionType.FAILURE_CUSTOM_RETURN_FORM,
        error: "No data received",
      });
    }
  } catch (error: any) {
    dispatch({
      type: OrderReturnActionType.FAILURE_CUSTOM_RETURN_FORM,
      error: error.message,
    });
  }
};

const createCustomReturn = async (
  idOrder: string,
  returnFormData: CustomReturnUserFormValues,
  selectedProducts: SelectedProducts,
  idReturnedMethod: string,
  paymentMethod: string,
  p24SessionId: string,
  p24Amount: string,
  dhlParcelPoint: DhlParcelPoint | null,
  dispatch: OrderReturnDispatch,
) => {
  // Plus setting p24_amount based on the idReturnedMethod
  dispatch({
    type: OrderReturnActionType.START_CREATE_CUSTOM_RETURN,
    idReturnedMethod,
  });

  try {
    const requestData = {
      method: "addReturn",
      data: {
        id_order: idOrder,
        returnForm: camelToSnakeCaseObject(returnFormData),
        selectedProducts: camelToSnakeCaseObject(selectedProducts),
        idReturnedMethod: Number(idReturnedMethod),
        paymentMethod: paymentMethod,
        p24_session_id: p24SessionId,
        p24_amount: parseFloat(p24Amount),
        dhlParcelPoint: dhlParcelPoint
          ? camelToSnakeCaseObject(dhlParcelPoint)
          : null,
      },
    };

    const response = await axiosInstance.post(
      `/rest${CustomReturnFormAPI}`,
      requestData,
    );

    if (response.data.success) {
      const idOrderReturn = response.data.data.id_order_return;
      const idCustomReturn = response.data.data.id_custom_return;

      setTimeout(() => {
        dispatch({
          type: OrderReturnActionType.SUCCESS_CREATE_CUSTOM_RETURN,
        });
      }, 2000);

      return { success: true, idOrderReturn, idCustomReturn };
    } else {
      dispatch({
        type: OrderReturnActionType.FAILURE_CREATE_CUSTOM_RETURN,
        error: response.data.message || "Unknown error",
      });

      return { success: false, error: response.data.message };
    }
  } catch (error: any) {
    dispatch({
      type: OrderReturnActionType.FAILURE_CUSTOM_RETURN,
      error: error.message,
    });

    return { success: false, error: error.message };
  }
};

const getRedirectUrl = async (
  idOrderReturn: string,
  idCustomReturn: string,
  paymentMethod: string,
  state: OrderReturnState,
  dispatch: OrderReturnDispatch,
) => {
  dispatch({ type: OrderReturnActionType.START_GET_REDIRECT_URL });

  try {
    const data =
      paymentMethod === "przelewy24"
        ? camelToSnakeCaseObject(state.customReturnForm?.P24)
        : camelToSnakeCaseObject({
            idCustomReturn,
          });

    if (paymentMethod === "przelewy24") {
      data.p24_url_return += `https://carry.pl/zwroty-produktow/${idOrderReturn}`;
    }

    const requestData = {
      method: "getRedirectUrl",
      paymentMethod,
      data,
    };

    const response = await axiosInstance.post(
      `/rest${CustomReturnFormAPI}`,
      requestData,
    );

    if (response.data.success) {
      dispatch({
        type: OrderReturnActionType.SUCCESS_GET_REDIRECT_URL,
      });

      return {
        success: true,
        redirectUrl:
          paymentMethod === "przelewy24"
            ? response.data.data.redirectUrl
            : `/zwroty-produktow/${idOrderReturn}`,
      };
    } else {
      dispatch({
        type: OrderReturnActionType.FAILURE_GET_REDIRECT_URL,
        error: response.data.message || "Unknown error",
      });
    }
  } catch (error: any) {
    dispatch({
      type: OrderReturnActionType.FAILURE_GET_REDIRECT_URL,
      error: error.message,
    });
  }
};

export function useOrderReturn() {
  const state = useOrderReturnState();
  const dispatch = useOrderReturnDispatch();

  const getOrderReturnsDataFunc = () => getOrderReturnsData(dispatch);
  const getCustomReturnDataFunc = (idOrderReturn: string) =>
    getCustomReturnData(idOrderReturn, dispatch);
  const getCustomReturnFormDataFunc = (idOrder: string) =>
    getCustomReturnFormData(idOrder, dispatch);
  const createCustomReturnFunc = (
    idOrder: string,
    returnFormData: CustomReturnUserFormValues,
    selectedProducts: SelectedProducts,
    idReturnedMethod: string,
    paymentMethod: "withoutpayment" | "przelewy24",
    p24SessionId: string,
    p24Amount: string,
    dhlParcelPoint: DhlParcelPoint | null,
  ) =>
    createCustomReturn(
      idOrder,
      returnFormData,
      selectedProducts,
      idReturnedMethod,
      paymentMethod,
      p24SessionId,
      p24Amount,
      dhlParcelPoint,
      dispatch,
    );

  const getRedirectUrlFunc = (
    idOrderReturn: string,
    idCustomReturn: string,
    paymentMethod: "withoutpayment" | "przelewy24",
  ) =>
    getRedirectUrl(
      idOrderReturn,
      idCustomReturn,
      paymentMethod,
      state,
      dispatch,
    );

  const clearOrderReturnsDataFunc = () =>
    dispatch({
      type: OrderReturnActionType.SUCCESS_ORDER_RETURNS,
      ordersToReturn: [],
      orderReturns: [],
    });
  const clearCustomReturnDataFunc = () =>
    dispatch({
      type: OrderReturnActionType.SUCCESS_CUSTOM_RETURN,
      customReturn: null,
    });
  const clearCustomReturnFormDataFunc = () =>
    dispatch({
      type: OrderReturnActionType.SUCCESS_CUSTOM_RETURN_FORM,
      customReturnForm: null,
    });

  return {
    state,
    getOrderReturnsData: getOrderReturnsDataFunc,
    clearOrderReturnsData: clearOrderReturnsDataFunc,
    getCustomReturnData: getCustomReturnDataFunc,
    clearCustomReturnData: clearCustomReturnDataFunc,
    getCustomReturnFormData: getCustomReturnFormDataFunc,
    clearCustomReturnFormData: clearCustomReturnFormDataFunc,
    createCustomReturn: createCustomReturnFunc,
    getRedirectUrl: getRedirectUrlFunc,
  };
}
